import { useCallback, useEffect, useMemo, useState } from 'react';
import { Gavel } from 'lucide-react';

import { getDecision, listDecisions } from '../../services/projectRegisterApi';
import DecisionDetailPanel from './DecisionDetailPanel';
import {
  Chip,
  DrawerDetail,
  EmptyState,
  ErrorBanner,
  FilterBar,
  Spinner,
  T,
  humanise,
  personLabel,
  useDetailNav,
} from './shared';

const DecisionRow = ({ decision, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className="w-full rounded-xl px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40"
    style={{
      background: selected ? T.selected : T.bg1,
      border: `1px solid ${selected ? 'rgba(125,211,252,0.4)' : T.br}`,
    }}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Chip bordered>{humanise(decision.decision_type)}</Chip>
          <Chip bordered>{humanise(decision.scope)}</Chip>
          {decision.is_reversal && (
            <Chip fg={T.warn} bg={T.warnBg}>
              Reversal
            </Chip>
          )}
        </div>
        <div className="mt-1.5 truncate text-[13px]" style={{ color: T.text }}>
          {decision.decision_text}
        </div>
        <div
          className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]"
          style={{ color: T.muted }}
        >
          {decision.decided_by?.length > 0 && (
            <span>{decision.decided_by.map(personLabel).join(', ')}</span>
          )}
          {decision.linked_deliverable_id && (
            <>
              <span>·</span>
              <span>{decision.linked_deliverable_id}</span>
            </>
          )}
        </div>
      </div>
      {decision.decision_confidence != null && (
        <span className="shrink-0 text-[11px]" style={{ color: T.muted }}>
          {(decision.decision_confidence * 100).toFixed(0)}%
        </span>
      )}
    </div>
  </button>
);

const ApprovalsList = ({ accessToken }) => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const {
    selectedId,
    detail,
    loading: detailLoading,
    error: detailError,
    open: openDetail,
    close: closeDetail,
  } = useDetailNav((entityId) => getDecision(accessToken, entityId));

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listDecisions(accessToken);
      setDecisions(response?.decisions || []);
    } catch (loadError) {
      setError(String(loadError?.message || loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return decisions;
    return decisions.filter((decision) => {
      const haystack = [
        decision.decision_text,
        decision.entity_id,
        decision.linked_deliverable_id,
        decision.decided_by?.join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [decisions, search]);

  if (loading) return <Spinner />;

  return (
    // Same frame as the other tabs: the page wrapper does not scroll, so the
    // list needs its own scroll area or decisions past the fold are cut off.
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex shrink-0 flex-col gap-3">
        <ErrorBanner>{error}</ErrorBanner>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search decisions"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <EmptyState icon={Gavel}>No decisions match.</EmptyState>
        ) : (
          <div className="flex flex-col gap-2.5">
            {filtered.map((decision) => (
              <DecisionRow
                key={decision.entity_id}
                decision={decision}
                selected={decision.entity_id === selectedId}
                onSelect={() => openDetail(decision.entity_id)}
              />
            ))}
          </div>
        )}
      </div>

      <DrawerDetail open={!!selectedId} onClose={closeDetail}>
        {detailLoading ? (
          <Spinner />
        ) : detailError ? (
          <div className="p-6">
            <ErrorBanner>{detailError}</ErrorBanner>
          </div>
        ) : (
          <DecisionDetailPanel detail={detail} onClose={closeDetail} />
        )}
      </DrawerDetail>
    </div>
  );
};

export default ApprovalsList;
